import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ClipboardText } from "@phosphor-icons/react";
import { fmtDateTime, fmtDate } from "@/lib/format";

const OUTCOME_COLOR = {
  "Connected": "bg-emerald-100 text-emerald-800",
  "Payment Received": "bg-emerald-100 text-emerald-800",
  "Settlement Accepted": "bg-emerald-100 text-emerald-800",
  "Settled": "bg-teal-100 text-teal-800",
  "Closed": "bg-slate-200 text-slate-800",
  "Promise To Pay": "bg-violet-100 text-violet-800",
  "Settlement Requested": "bg-blue-100 text-blue-800",
  "Follow-up Required": "bg-amber-100 text-amber-800",
  "Decline to Pay": "bg-red-100 text-red-800",
  "Cheque Bounced": "bg-red-100 text-red-800",
  "Process Legal": "bg-rose-200 text-rose-900",
  "Dispute Raised": "bg-amber-100 text-amber-800",
  "Customer Untraceable": "bg-orange-100 text-orange-800",
  "Not Connected": "bg-red-100 text-red-800",
  "Busy": "bg-amber-100 text-amber-800",
  "Wrong Number": "bg-red-100 text-red-800",
};

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export function ActivityLoggerButton({ accountId, presetType, onSaved, variant = "default", testIdSuffix, borrowerName, presetContact, outstanding }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({ types: [], outcomes: [] });
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/activity-options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const openDlg = (e) => {
    if (e) e.stopPropagation();
    const prefillRemarks = presetContact
      ? `Contacted reference: ${presetContact.relationship} — ${presetContact.name} (${presetContact.phone})\n`
      : "";
    setF({ activity_type: presetType || "Call", outcome: "", activity_at: nowLocal(), remarks: prefillRemarks, next_follow_up_date: "" });
    setOpen(true);
  };
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const payAmt = +f.payment_amount || 0;
  const clearsBalance = f.outcome === "Payment Received" && payAmt > 0 && (typeof outstanding === "number" ? payAmt >= outstanding : true);

  const submit = async () => {
    if (!f.outcome) return toast.error("Select an outcome");
    setSaving(true);
    try {
      const payload = { ...f, activity_at: f.activity_at ? new Date(f.activity_at).toISOString() : undefined };
      if (presetContact) payload.contacted_reference = { relationship: presetContact.relationship, name: presetContact.name, phone: presetContact.phone };
      await api.post(`/accounts/${accountId}/activities`, payload);
      toast.success("Activity logged");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log activity");
    } finally { setSaving(false); }
  };

  const trigger = variant === "icon" ? (
    <button
      title="Log Activity"
      onClick={openDlg}
      className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-transparent text-primary hover:bg-primary/10 transition-colors"
      data-testid={`log-activity-${testIdSuffix || accountId}`}
    >
      <ClipboardText size={16} weight="bold" />
    </button>
  ) : (
    <Button size="sm" className="rounded-sm gap-2 h-9" onClick={openDlg} data-testid="log-activity-button">
      <ClipboardText size={15} weight="bold" /> Log Activity
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger}
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto" data-testid="activity-logger-dialog">
        <DialogHeader>
          <DialogTitle>Log Activity{borrowerName ? ` — ${borrowerName}` : ""}</DialogTitle>
          <DialogDescription>Manually record any interaction with the borrower. Captured against your name automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Activity Type</Label>
              <Select value={f.activity_type} onValueChange={(v) => upd("activity_type", v)}>
                <SelectTrigger className="rounded-sm" data-testid="activity-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{opts.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Outcome</Label>
              <Select value={f.outcome || undefined} onValueChange={(v) => upd("outcome", v)}>
                <SelectTrigger className="rounded-sm" data-testid="activity-outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
                <SelectContent>{opts.outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {f.outcome === "Promise To Pay" && (
            <div className="grid grid-cols-2 gap-3 border border-violet-200 bg-violet-50/50 p-3 rounded-sm">
              <div><Label className="text-xs">PTP Amount (₹)</Label><Input type="number" className="rounded-sm" value={f.ptp_amount || ""} onChange={(e) => upd("ptp_amount", e.target.value)} data-testid="activity-ptp-amount" /></div>
              <div><Label className="text-xs">PTP Date</Label><Input type="date" className="rounded-sm" value={f.ptp_date || ""} onChange={(e) => upd("ptp_date", e.target.value)} data-testid="activity-ptp-date" /></div>
            </div>
          )}
          {f.outcome === "Settlement Requested" && (
            <div className="grid grid-cols-2 gap-3 border border-blue-200 bg-blue-50/50 p-3 rounded-sm">
              <div><Label className="text-xs">Settlement Amount (₹)</Label><Input type="number" className="rounded-sm" value={f.settlement_amount || ""} onChange={(e) => upd("settlement_amount", e.target.value)} data-testid="activity-settlement-amount" /></div>
              <div><Label className="text-xs">Reason</Label><Input className="rounded-sm" value={f.settlement_reason || ""} onChange={(e) => upd("settlement_reason", e.target.value)} data-testid="activity-settlement-reason" /></div>
            </div>
          )}
          {f.outcome === "Payment Received" && (
            <div className="border border-emerald-200 bg-emerald-50/50 p-3 rounded-sm space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Payment Amount (₹)</Label><Input type="number" className="rounded-sm" value={f.payment_amount || ""} onChange={(e) => upd("payment_amount", e.target.value)} data-testid="activity-payment-amount" /></div>
                <div><Label className="text-xs">Mode</Label>
                  <Select value={f.payment_mode || "UPI"} onValueChange={(v) => upd("payment_mode", v)}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{["UPI", "NEFT", "Cash", "Cheque", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              {clearsBalance && (
                <div className="border-t border-emerald-200 pt-3" data-testid="auto-close-block">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox checked={!!f.close_on_full_payment} onCheckedChange={(c) => upd("close_on_full_payment", !!c)} data-testid="auto-close-checkbox" />
                    <span className="text-xs text-emerald-900 font-medium">This payment clears the full balance — also mark this account as closed.</span>
                  </label>
                  {f.close_on_full_payment && (
                    <div className="mt-2 pl-6 flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Final status</span>
                      <Select value={f.close_status || "closed"} onValueChange={(v) => upd("close_status", v)}>
                        <SelectTrigger className="rounded-sm w-32 h-8" data-testid="auto-close-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="settled">Settled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {f.outcome === "Process Legal" && (
            <div className="grid grid-cols-2 gap-3 border border-rose-200 bg-rose-50/50 p-3 rounded-sm">
              <div><Label className="text-xs">Court Case No.</Label><Input className="rounded-sm" value={f.court_case_no || ""} onChange={(e) => upd("court_case_no", e.target.value)} data-testid="activity-court-case" /></div>
              <div><Label className="text-xs">Next Hearing Date</Label><Input type="date" className="rounded-sm" value={f.next_hearing_date || ""} onChange={(e) => upd("next_hearing_date", e.target.value)} data-testid="activity-hearing-date" /></div>
              <div><Label className="text-xs">Advocate</Label><Input className="rounded-sm" value={f.advocate || ""} onChange={(e) => upd("advocate", e.target.value)} data-testid="activity-advocate" /></div>
              <div><Label className="text-xs">Legal Status</Label>
                <Select value={f.legal_status || "Legal Initiated"} onValueChange={(v) => upd("legal_status", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="activity-legal-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Legal Initiated", "Notice Sent", "Sec 138 - Notice Pending", "Case Filed", "Hearing Scheduled", "Awarded", "Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          {f.outcome === "Cheque Bounced" && (
            <div className="border border-red-200 bg-red-50/60 p-3 rounded-sm text-xs text-red-700" data-testid="cheque-bounce-note">
              This will flag the account for <b>Sec 138 (cheque bounce)</b> proceedings, mark it a Legal Candidate, and create a high-priority legal-notice follow-up (due in 7 days).
            </div>
          )}

          {(f.outcome === "Settled" || f.outcome === "Closed") && (
            <div className="border border-teal-200 bg-teal-50/60 p-3 rounded-sm text-xs text-teal-800" data-testid="settle-close-note">
              Marking this account <b>{f.outcome}</b> will set its status to <b>{f.outcome === "Settled" ? "settled" : "closed"}</b> and <b>clear its outstanding balance to ₹0</b>, removing it from Total Outstanding on the dashboard.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Activity Date & Time</Label><Input type="datetime-local" className="rounded-sm" value={f.activity_at || ""} onChange={(e) => upd("activity_at", e.target.value)} data-testid="activity-datetime" /></div>
            <div><Label className="text-xs">Next Follow-up Date</Label><Input type="date" className="rounded-sm" value={f.next_follow_up_date || ""} onChange={(e) => upd("next_follow_up_date", e.target.value)} data-testid="activity-next-followup" /></div>
          </div>
          <div><Label className="text-xs">Remarks</Label><Textarea className="rounded-sm" value={f.remarks || ""} onChange={(e) => upd("remarks", e.target.value)} data-testid="activity-remarks" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} className="rounded-sm" data-testid="activity-submit">{saving ? "Saving…" : "Save Activity"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ActivitiesList({ items }) {
  if (!items.length) return <p className="text-sm text-muted-foreground text-center py-8">No activities logged yet</p>;
  return (
    <div className="space-y-2">
      {items.map((a) => (
        <div key={a.id} className="border border-border p-3" data-testid={`activity-item-${a.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{a.activity_type}</span>
              {a.outcome && <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm ${OUTCOME_COLOR[a.outcome] || "bg-secondary"}`}>{a.outcome}</span>}
            </div>
            <span className="text-xs text-muted-foreground mono shrink-0">{fmtDateTime(a.activity_at)}</span>
          </div>
          {a.remarks && <p className="text-sm text-muted-foreground mt-1">{a.remarks}</p>}
          {a.contacted_reference && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/10 rounded-sm px-2 py-0.5" data-testid={`activity-ref-${a.id}`}>
              Ref: {a.contacted_reference.relationship} — {a.contacted_reference.name} ({a.contacted_reference.phone})
            </div>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="label-caps">{a.created_by}</span>
            {a.next_follow_up_date && <span className="text-[11px] text-amber-600 font-semibold">Next follow-up: {fmtDate(a.next_follow_up_date)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
